import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, CheckCircle2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function Contact() {
  useSeo("Contact Collins Crafty Creations — Memphis, TN", "Get in touch with Collins Crafty Creations about custom apparel, personalized gifts, bulk orders, or a bespoke project in Memphis & beyond.");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", interest: "General" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Message received!");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-slate-50 border-b border-silver-light">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-xs text-navy/60">
          <Link to="/" className="hover:text-navy-royal">Home</Link><ChevronRight size={12} />
          <span className="text-navy-dark font-semibold">Contact</span>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12" data-testid="contact-page">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-navy-royal font-semibold">Get in touch</div>
          <h1 className="font-serif text-5xl text-navy-dark mt-3 leading-tight">Let's talk about what you need.</h1>
          <p className="text-navy/70 mt-6 leading-relaxed">
            Questions about a product, a custom order, or a bulk request? Send us a note — we typically reply within one business day.
          </p>
          <div className="mt-10 space-y-4 text-sm text-navy/80">
            <div className="flex items-center gap-3"><Mail size={16} className="text-navy-royal" /> hello@collinscrafty.co</div>
            <div className="flex items-center gap-3"><Phone size={16} className="text-navy-royal" /> (901) 555-0123</div>
            <div className="flex items-center gap-3"><MapPin size={16} className="text-navy-royal" /> Memphis, TN</div>
          </div>
          <div className="mt-10 p-6 rounded-2xl bg-navy-dark text-white">
            <div className="font-script text-3xl text-white">Handmade with heart.</div>
            <p className="text-white/80 text-sm mt-3">Custom apparel, tumblers, personalized gifts & bespoke printing — crafted just for you.</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white border border-silver-light p-8 shadow-lg">
          {submitted ? (
            <div className="p-8 text-center" data-testid="contact-success">
              <CheckCircle2 size={48} className="mx-auto text-navy-royal" />
              <div className="font-serif text-2xl text-navy-dark mt-4">Message received.</div>
              <div className="text-sm text-navy/70 mt-2">We'll get back to you shortly.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input required value={form.name} onChange={update("name")} data-testid="ct-name" /></div>
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={update("email")} data-testid="ct-email" /></div>
              </div>
              <div>
                <Label>I'm interested in</Label>
                <Select value={form.interest} onValueChange={update("interest")}>
                  <SelectTrigger data-testid="ct-interest"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General inquiry</SelectItem>
                    <SelectItem value="Custom">Custom Order</SelectItem>
                    <SelectItem value="Bulk">Bulk / Wholesale Order</SelectItem>
                    <SelectItem value="Shop">Shop Product Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label><Input required value={form.subject} onChange={update("subject")} data-testid="ct-subject" /></div>
              <div><Label>Message</Label><Textarea required rows={6} value={form.message} onChange={update("message")} data-testid="ct-message" /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="ct-submit">
                {submitting ? "Sending…" : "Send Message"}
              </Button>
            </form>
          )}
        </motion.div>
      </section>
    </>
  );
}
